import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * Privacy Policy for pitchgrades.com.
 *
 * The upload form's single consent checkbox references this page — every use
 * of submitted data described here must stay accurate to what the product
 * actually does. NOTE: this is a plain-language draft; have it reviewed by
 * legal counsel before relying on it.
 */
const PrivacyPolicy: React.FC = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
      Privacy Policy
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
      pitchgrades.com is operated by The International Accelerator ("IA", "we"). This policy
      explains what we collect when you use the pitch deck grader and how we use it. By checking
      the consent box on the upload form, you agree to the practices described below.
    </Typography>

    <Section title="What we collect">
      <Typography variant="body1" paragraph>
        When you submit a pitch deck for analysis we collect: the deck file and its contents, your
        email address, your country of origin, and (if you provide them) your industry and company
        stage.
      </Typography>
    </Section>

    <Section title="How we use your information">
      <Typography variant="body1" component="div">
        <ul>
          <li>
            <strong>To provide the analysis.</strong> Your deck is processed by an AI service
            (Anthropic's Claude) to generate the feedback and scores you receive.
          </li>
          <li>
            <strong>Internal program evaluation.</strong> Your submission, including an assessment
            of fit with The International Accelerator's program, is reviewed by IA staff as part of
            our deal-flow screening.
          </li>
          <li>
            <strong>Secure storage and comparison.</strong> Your deck and its analysis are stored
            securely so we can benchmark scores (e.g. percentile comparisons) and improve analysis
            quality over time.
          </li>
          <li>
            <strong>Anonymous service improvement.</strong> Stored decks may be used in anonymized
            form as reference context to improve scoring accuracy for all users.
          </li>
          <li>
            <strong>Communications.</strong> We may contact you at the email you provide about your
            analysis and about The International Accelerator's programs and related opportunities.
            You can opt out of marketing communications at any time via the unsubscribe link in any
            such email.
          </li>
        </ul>
      </Typography>
    </Section>

    <Section title="Who can see your data">
      <Typography variant="body1" paragraph>
        Your submission is visible to The International Accelerator team. We do not sell your data
        or share your pitch deck with third parties, except for the service providers that operate
        this product (AI analysis, cloud hosting, and spreadsheet/database storage), who process it
        on our behalf.
      </Typography>
    </Section>

    <Section title="Retention and deletion">
      <Typography variant="body1" paragraph>
        We retain submissions for as long as they are useful for the purposes above. You may
        request deletion of your data at any time by contacting us, and we will remove your deck,
        analysis, and contact details from our systems.
      </Typography>
    </Section>

    <Section title="Contact">
      <Typography variant="body1" paragraph>
        For privacy questions or deletion requests, contact The International Accelerator via the
        contact page on this site.
      </Typography>
    </Section>

    <Typography variant="caption" color="text.secondary">
      Last updated: June 2026
    </Typography>
  </Container>
);

export default PrivacyPolicy;
